import { ActionStatus } from '@pixacare/shared';
import { SyncActionStateProgress } from '@shared/models/sync/sync-action-state-progress';
import { VIDEO_UPLOAD_PLACEHOLDER_THUMBNAIL } from '@shared/utils/video-upload/video-upload-placeholder-thumbnail';
import { VideoUploadEntry } from '../../../video-uploads/models/video-upload-entry';
import { parseSequenceParentKey } from '../../../video-uploads/utils/parse-sequence-parent-key';
import { SyncDisplayResolver } from '../../models/sync-display-resolver';
import { rollupSyncActionState } from '../../utils/rollup-sync-action-state';
import { syncActionToDisplayChild } from '../../utils/sync-action-to-display-child';
import { toRefKey } from '../../utils/to-ref-key';

const fractionDone = (video: VideoUploadEntry): number => {
  if (video.status === ActionStatus.SUCCESS) {
    return 1;
  }
  return video.fileSize === 0 ? 0 : video.syncedBytes / video.fileSize;
};

const weightedSequenceProgress = (videos: VideoUploadEntry[]): SyncActionStateProgress => {
  const totalSize = videos.reduce((total, video) => total + video.fileSize, 0);
  const weightOf = (video: VideoUploadEntry): number => (totalSize > 0 ? video.fileSize : 1);
  return {
    done: videos.reduce((total, video) => total + weightOf(video) * fractionDone(video), 0),
    total: videos.reduce((total, video) => total + weightOf(video), 0),
  };
};

export const resolveSequenceDisplay: SyncDisplayResolver = (parent, children, slices) => {
  const { clientCode, sequenceId } = parseSequenceParentKey(parent.id);
  const snapshot = slices.videoUploads[clientCode]?.[sequenceId];
  const previewUrl = children
    .map((child) => snapshot?.videos[String(child.object.id)]?.previewUrl)
    .find((url) => !!url);

  // Status is the generic child rollup; the percentage is byte-weighted from the transport snapshot
  // so its denominator is stable from queue time. The rollup progress is the fallback for the
  // transient frame before that snapshot exists.
  const rolledUpState = rollupSyncActionState(children.map((child) => child.state));

  return {
    key: toRefKey(parent),
    clientName: slices.clientNames[clientCode],
    thumbnailUrl: VIDEO_UPLOAD_PLACEHOLDER_THUMBNAIL,
    previewUrl,
    // The durable title lives on the transport snapshot (backfilled for hydrated uploads by
    // setSequenceUploadInfo); the parent label is a fallback until that snapshot exists.
    title: snapshot?.title ?? parent.label,
    date: snapshot?.createdOn,
    state: {
      status: rolledUpState.status,
      progress: snapshot ? weightedSequenceProgress(Object.values(snapshot.videos)) : rolledUpState.progress,
    },
    children: children.map(syncActionToDisplayChild),
  };
};
